import React from 'react'
import { useEffect, useState } from 'react'
import useIsMobile from '../../hooks/useIsMobile'
import { Close } from '../../assets/icons'
import theme from '../../styled/theme'
import NecessityScreen from './screens/necessityScreen'
import PlatformAppRatingForm from './screens/platformAppRatingForm'
import ReasonsScreen from './screens/reasonsScreen'
import SuccessScreen from './screens/successScreen'
import { FloatingButton, ButtonText, Panel, Overlay, Header, CloseButton } from './styled'
import { Text2 } from '../ui/text'

const SatisfactionSurvey = ({
	module,
	questions = [],
	successMessage,
	buttonHidden,
	hasError = false,
	autoOpen = false,
	onSubmitAnswer,
	onComplete
}) => {
	const isMobile = useIsMobile()
	const [isOpen, setIsOpen] = useState(false)
	const [openedFromButton, setOpenedFromButton] = useState(false)
	const [idHistory, setIdHistory] = useState([])
	const [answers, setAnswers] = useState([])
	const [nextQuestionId, setNextQuestionId] = useState(undefined)
	const [initialQuestionId, setInitialQuestionId] = useState(null)

	useEffect(() => {
		if (questions?.length > 0) {
			setInitialQuestionId(questions[0].id)
		}
	}, [questions])

	useEffect(() => {
		if (autoOpen && !isOpen) {
			setOpenedFromButton(false)
			setIsOpen(true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [autoOpen])

	useEffect(() => {
		if (nextQuestionId === null) {
			if (onComplete) {
				onComplete(answers, module)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nextQuestionId])

	const handleReset = () => {
		setOpenedFromButton(false)
		setNextQuestionId(undefined)
		setAnswers([])
		setIdHistory([])
	}

	const handleToggle = () => {
		if (isOpen) {
			handleReset()
		} else {
			setOpenedFromButton(true)
		}
		setIsOpen(!isOpen)
	}

	const handlePreviousStep = () => {
		if (idHistory.length > 0) {
			setNextQuestionId(idHistory[idHistory.length - 1])
		}
		setIdHistory(idHistory.slice(0, -1))
		setAnswers(answers.slice(0, -1))
	}

	const saveAnswer = value => {
		setIdHistory([...idHistory, value.questionId])
		setAnswers([...answers, value])
		setNextQuestionId(value.nextQuestionId)
		if (onSubmitAnswer) {
			onSubmitAnswer(value, module, openedFromButton ? 'manual' : 'automatic')
		}
	}

	const renderView = (question, showExperienceTitle) => {
		switch (question?.answerMode) {
			case 'scale-5':
				return (
					<PlatformAppRatingForm
						handleSubmit={saveAnswer}
						info={question}
						historyIdHandler={handlePreviousStep}
						showExperienceTitle={showExperienceTitle}
					/>
				)
			case 'scale-selection-5':
				return (
					<PlatformAppRatingForm
						handleSubmit={saveAnswer}
						info={question}
						isSmartcard
						historyIdHandler={handlePreviousStep}
						showExperienceTitle={showExperienceTitle}
					/>
				)
			case 'multipleSelection':
				return (
					<ReasonsScreen
						handleSubmit={saveAnswer}
						info={question}
						historyIdHandler={handlePreviousStep}
						hideBackButton={idHistory.length === 0}
						showExperienceTitle={showExperienceTitle}
					/>
				)
			case 'close':
				return (
					<NecessityScreen
						handleSubmit={saveAnswer}
						info={question}
						historyIdHandler={handlePreviousStep}
						hideBackButton={idHistory.length === 0}
						showExperienceTitle={showExperienceTitle}
					/>
				)
			default:
				return null
		}
	}

	const atStartOfFlow = nextQuestionId === undefined
	const atEndOfFlow = nextQuestionId === null

	const renderCurrentComponent = () => {
		const showExperienceTitle = !isMobile && !atStartOfFlow

		if (atEndOfFlow) {
			return (
				<SuccessScreen successMessage={successMessage} hasError={hasError} onClose={handleToggle} />
			)
		}

		if (atStartOfFlow) {
			const initialQuestion = questions.find(question => question.id === initialQuestionId)
			return renderView(initialQuestion, showExperienceTitle)
		}

		const nextQuestion = questions.find(question => question.id === nextQuestionId)
		return renderView(nextQuestion, showExperienceTitle)
	}

	return (
		<>
			{!buttonHidden && (
				<FloatingButton onClick={handleToggle} $isOpen={isOpen} $isMobile={isMobile}>
					<Text2 size="14px" fontWeight="600" color={theme.colors.brand.white}>
						Contanos tu experiencia
					</Text2>
				</FloatingButton>
			)}
			<Panel $isOpen={isOpen} $isMobile={isMobile}>
				<Header>
					{isMobile && (
						<Text2 size="18px" fontWeight="600" color={theme.colors.text.primaryText}>
							Contanos tu experiencia
						</Text2>
					)}
					<CloseButton onClick={handleToggle}>
						<Close size={24} color={theme.colors.text.secondaryText} />
					</CloseButton>
				</Header>
				{isOpen && renderCurrentComponent()}
			</Panel>
			<Overlay $isOpen={isOpen} onClick={handleToggle} />
		</>
	)
}

export default SatisfactionSurvey
